import * as React from "react";
import { describeError } from "../api/errors";

/**
 * The app's one data-fetching primitive, generalised from ScadsysIdentity's users/useUserSearch.ts.
 *
 * That hook is the only correct fetch in either repo: it debounces, and it guards against stale
 * responses with a monotonically increasing request id, so a slow early request can never overwrite
 * the result of a later one. ScadsysIdentity then re-implements the same ref idiom inline three more
 * times in users/Index.tsx; here it lives once.
 *
 *   const modules = useAsync(() => listModules(), [organizationId]);
 *   const results = useAsync(() => search(query), [query], { debounceMs: 300 });
 *
 * `deps` behaves like a useEffect dependency list: change one and the load re-runs. `refresh()`
 * re-runs it on demand - use it after a create/update/delete.
 */

export interface AsyncState<T> {
    data: T | undefined;
    isLoading: boolean;
    error: string;
    refresh: () => void;
    /** Replace the data locally, e.g. after an optimistic edit, without a round trip. */
    setData: React.Dispatch<React.SetStateAction<T | undefined>>;
}

export interface UseAsyncOptions {
    /** Wait this long after the last dependency change before firing. Use for search-as-you-type. */
    debounceMs?: number;
    /** Skip the load entirely - for a request that needs an id the user has not picked yet. */
    enabled?: boolean;
    /** Value to expose before the first load resolves. */
    initial?: any;
}

export function useAsync<T>(loader: () => Promise<T>, deps: React.DependencyList, options: UseAsyncOptions = {}): AsyncState<T> {
    const { debounceMs = 0, enabled = true, initial } = options;

    const [data, setData] = React.useState<T | undefined>(initial);
    const [isLoading, setIsLoading] = React.useState(enabled);
    const [error, setError] = React.useState("");
    const [nonce, setNonce] = React.useState(0);

    // Every in-flight request carries a version; only the newest is allowed to write state.
    const requestVersion = React.useRef(0);
    // Kept in a ref so the loader identity does not have to be stable for callers.
    const loaderRef = React.useRef(loader);
    loaderRef.current = loader;

    React.useEffect(() => {
        if (!enabled) {
            setIsLoading(false);
            return;
        }

        const version = ++requestVersion.current;
        setIsLoading(true);
        setError("");

        const run = () => {
            loaderRef
                .current()
                .then((result) => {
                    if (requestVersion.current !== version) return;
                    setData(result);
                    setIsLoading(false);
                })
                .catch((e) => {
                    if (requestVersion.current !== version) return;
                    setError(describeError(e));
                    setIsLoading(false);
                });
        };

        if (debounceMs > 0) {
            const timer = setTimeout(run, debounceMs);
            return () => clearTimeout(timer);
        }

        run();
        // Bumping requestVersion on unmount discards whatever is still in flight.
        return () => {
            requestVersion.current++;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [...deps, enabled, debounceMs, nonce]);

    const refresh = React.useCallback(() => setNonce((n) => n + 1), []);

    return { data, isLoading, error, refresh, setData };
}

/**
 * The mutation counterpart: tracks `busy` and `error` around a one-shot call so screens stop
 * hand-rolling the same `setBusy(true) / setError("") / finally setBusy(false)` triplet.
 *
 *   const save = useAction(async () => { await createFaculty(draft); refresh(); });
 *   <Button loading={save.busy} onClick={save.run}>Save</Button>
 */
export interface ActionState {
    busy: boolean;
    error: string;
    clearError: () => void;
    run: () => void;
    /** Awaitable variant - resolves true on success, false if the action threw. */
    runAsync: () => Promise<boolean>;
}

export function useAction(action: () => Promise<unknown>, describe: (e: unknown) => string = describeError): ActionState {
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState("");

    const actionRef = React.useRef(action);
    actionRef.current = action;

    const runAsync = React.useCallback(async () => {
        setBusy(true);
        setError("");
        try {
            await actionRef.current();
            return true;
        } catch (e) {
            setError(describe(e));
            return false;
        } finally {
            setBusy(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const run = React.useCallback(() => {
        void runAsync();
    }, [runAsync]);

    return { busy, error, clearError: () => setError(""), run, runAsync };
}
